import React from 'react';
import { Box, Typography } from '@mui/material';
import { getCinemaStyles } from './styles';
import {
  CONTROLS_KICKER,
  CONTROLS_LOOK_LABEL,
  CONTROLS_MOVE_LABEL,
  CONTROLS_ROTATE_LABEL,
} from './constants';

const KEY_PRESS_STAGGER_S = 0.3;
const ARROWS_WAVE_OFFSET_S = 1.8;

const WASD_KEYS = [
  { label: 'W', area: '1 / 2', delayS: 0 },
  { label: 'A', area: '2 / 1', delayS: KEY_PRESS_STAGGER_S },
  { label: 'S', area: '2 / 2', delayS: KEY_PRESS_STAGGER_S * 2 },
  { label: 'D', area: '2 / 3', delayS: KEY_PRESS_STAGGER_S * 3 },
];

const ARROW_KEYS = [
  { label: '↑', area: '1 / 2', delayS: ARROWS_WAVE_OFFSET_S },
  {
    label: '←',
    area: '2 / 1',
    delayS: ARROWS_WAVE_OFFSET_S + KEY_PRESS_STAGGER_S,
  },
  {
    label: '↓',
    area: '2 / 2',
    delayS: ARROWS_WAVE_OFFSET_S + KEY_PRESS_STAGGER_S * 2,
  },
  {
    label: '→',
    area: '2 / 3',
    delayS: ARROWS_WAVE_OFFSET_S + KEY_PRESS_STAGGER_S * 3,
  },
];

const KeycapCluster = ({
  keys,
  classes,
}: {
  keys: typeof WASD_KEYS;
  classes: ReturnType<typeof getCinemaStyles>;
}) => (
  <Box sx={classes.wasdGrid}>
    {keys.map((key) => (
      <Box
        key={key.label}
        sx={{
          ...classes.keycap,
          gridArea: key.area,
          animationDelay: `${key.delayS}s`,
        }}
      >
        {key.label}
      </Box>
    ))}
  </Box>
);

interface IControlsGuideProps {
  classes: ReturnType<typeof getCinemaStyles>;
}

const ControlsGuide = ({ classes }: IControlsGuideProps) => (
  <Box sx={classes.guide}>
    <Typography component="span" sx={classes.guideKicker}>
      {CONTROLS_KICKER}
    </Typography>
    <Box sx={classes.guideCard}>
      <Box sx={classes.guideGroup}>
        <Box sx={classes.guideGlyphs}>
          <KeycapCluster keys={WASD_KEYS} classes={classes} />
        </Box>
        <Typography component="span" sx={classes.guideLabel}>
          {CONTROLS_MOVE_LABEL}
        </Typography>
      </Box>
      <Box sx={classes.guideDivider} />
      <Box sx={classes.guideGroup}>
        <Box sx={classes.guideGlyphs}>
          <Box component="span" sx={classes.guideMouse}>
            <svg
              width="24"
              height="34"
              viewBox="0 0 24 34"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="1.25"
                y="1.25"
                width="21.5"
                height="31.5"
                rx="10.75"
                stroke="rgba(255,255,255,.6)"
                strokeWidth="1.5"
                fill="rgba(255,255,255,.05)"
              />
              <path
                className="myta-lmb"
                d="M11.2 2.5a9.45 9.45 0 0 0-8.7 9.4v0.85h8.7Z"
              />
              <line
                x1="12"
                y1="1.9"
                x2="12"
                y2="12.75"
                stroke="rgba(255,255,255,.6)"
                strokeWidth="1.25"
              />
              <line
                x1="1.9"
                y1="12.75"
                x2="22.1"
                y2="12.75"
                stroke="rgba(255,255,255,.45)"
                strokeWidth="1.25"
              />
            </svg>
          </Box>
        </Box>
        <Typography component="span" sx={classes.guideLabel}>
          {CONTROLS_LOOK_LABEL}
        </Typography>
      </Box>
      <Box sx={classes.guideDivider} />
      <Box sx={classes.guideGroup}>
        <Box sx={classes.guideGlyphs}>
          <KeycapCluster keys={ARROW_KEYS} classes={classes} />
        </Box>
        <Typography component="span" sx={classes.guideLabel}>
          {CONTROLS_ROTATE_LABEL}
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default ControlsGuide;
